import React, { useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { set_pay_type_action } from '../../actions/setPayTypeAction';
import { set_payment_gateway_action } from '../../actions/setPaymentGatewayAction';
import { set_gold_gram_usage } from '../../actions/cartActions';
import NumberFormat from 'react-number-format';
import './PaymentTypeSection.css';
import { Button } from 'react-bootstrap';
import { CreditCard, Wallet, Coins, Receipt } from 'lucide-react';
import { ENABLE_GOLD_WALLET_IN_CART } from '../../themeSetting';
import { usePaymentGatewaySettings } from '../../hooks/usePaymentGatewaySettings';
import { getGatewayLogo } from '../../constants/gatewayLogos';
import { getDeposit } from '../../actions/user';
import { formatGatewayLimit } from '../../services/paymentGateway';

const SHORT_GATEWAY_NAMES = {
  sep: 'سامان',
  pec: 'پارسیان',
  newpg: 'پارسیان NewPG',
  iec: 'پارسیان NewPG',
  zarinpal: 'زرین‌پال',
  pna: 'پرداخت نوین',
  digipay: 'دیجی‌پی',
};

const gatewayLabel = (provider) =>
  SHORT_GATEWAY_NAMES[provider?.key] || provider?.name || provider?.key || '';

const PaymentTypeSection = ({ nextStep }) => {
  const dispatch = useDispatch();
  const payType = useSelector((state) => state.pay_type);
  const selectedGateway = useSelector((state) => state.payment_gateway);
  const depositPrice = useSelector((state) => state.deposit_price);
  const walletGearmValue = useSelector((state) => state.cart.wallet_gearm_value) || 0;
  const goldGramCartUsage = useSelector((state) => state.cart.gold_gram_cart_usage) || 0;
  const { onlineEnabled, transferEnabled, depositEnabled, loading, enabledProviders, defaultProvider, maxAmountToman } =
    usePaymentGatewaySettings();

  const sortedProviders = useMemo(() => {
    const list = [...(enabledProviders || [])];
    list.sort((a, b) => {
      if (a.key === defaultProvider) return -1;
      if (b.key === defaultProvider) return 1;
      return 0;
    });
    return list;
  }, [enabledProviders, defaultProvider]);

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(getDeposit());
  }, [dispatch]);

  useEffect(() => {
    if (loading) return;
    if (!transferEnabled && payType === 'transfer_receipt') {
      dispatch(set_pay_type_action(onlineEnabled ? 'cashe' : 'deposit'));
    }
    if ((!depositEnabled || depositPrice <= 0) && payType === 'deposit') {
      dispatch(set_pay_type_action(onlineEnabled ? 'cashe' : transferEnabled ? 'transfer_receipt' : 'cashe'));
    }
    if (!onlineEnabled && payType === 'cashe') {
      if (transferEnabled) {
        dispatch(set_pay_type_action('transfer_receipt'));
      } else if (depositEnabled && depositPrice > 0) {
        dispatch(set_pay_type_action('deposit'));
      }
    }
  }, [loading, onlineEnabled, transferEnabled, depositEnabled, payType, dispatch, depositPrice]);

  useEffect(() => {
    if (loading || !onlineEnabled || sortedProviders.length === 0) return;
    if (payType !== 'cashe' && payType !== 'deposit') return;
    const keys = sortedProviders.map((p) => p.key);
    if (!keys.includes(selectedGateway)) {
      dispatch(set_payment_gateway_action(defaultProvider || keys[0]));
    }
  }, [loading, onlineEnabled, sortedProviders, defaultProvider, selectedGateway, dispatch, payType]);

  const handlePaymentTypeChange = (value) => {
    dispatch(set_pay_type_action(value));
    if (
      (value === 'cashe' || value === 'deposit') &&
      !selectedGateway &&
      (defaultProvider || sortedProviders[0])
    ) {
      dispatch(set_payment_gateway_action(defaultProvider || sortedProviders[0].key));
    }
  };

  const handleGatewayChange = (key, e) => {
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation();
    }
    dispatch(set_payment_gateway_action(key));
    if (payType !== 'cashe' && payType !== 'deposit') {
      dispatch(set_pay_type_action('cashe'));
    }
  };

  const handleClick = () => {
    if (
      (payType === 'cashe' || payType === 'deposit') &&
      onlineEnabled &&
      !selectedGateway &&
      sortedProviders[0]
    ) {
      dispatch(set_payment_gateway_action(defaultProvider || sortedProviders[0].key));
    }
    nextStep();
  };

  const isDepositDisabled = !depositEnabled || depositPrice === 0;
  const onlineDisabled = !onlineEnabled;
  const selectedProvider = sortedProviders.find((p) => p.key === selectedGateway) || null;
  const needsGateway = !onlineDisabled && sortedProviders.length > 0;
  const showBanksForOnline =
    !onlineDisabled && payType === 'cashe' && sortedProviders.length > 0;
  const showBanksForDeposit =
    !onlineDisabled && payType === 'deposit' && !isDepositDisabled && sortedProviders.length > 0;
  const canContinue =
    !loading &&
    ((payType === 'cashe' &&
      onlineEnabled &&
      (selectedGateway || sortedProviders.length > 0)) ||
      (payType === 'deposit' &&
        !isDepositDisabled &&
        (!needsGateway || selectedGateway || sortedProviders.length > 0)) ||
      (payType === 'transfer_receipt' && transferEnabled));

  const renderGatewayBanks = () => (
    <div className='payment-type-section__banks' role='group' aria-label='انتخاب درگاه'>
      <div className='payment-type-section__banks-label'>انتخاب درگاه</div>
      <div className='payment-type-section__banks-list'>
        {sortedProviders.map((provider) => {
          const isDefault = provider.key === defaultProvider;
          const isSelected = selectedGateway === provider.key;
          const logo = getGatewayLogo(provider.key);
          return (
            <button
              key={provider.key}
              type='button'
              className={`payment-type-section__bank ${
                isSelected ? 'payment-type-section__bank--selected' : ''
              }`}
              onClick={(e) => handleGatewayChange(provider.key, e)}
              aria-pressed={isSelected}
            >
              {logo ? (
                <img className='payment-type-section__bank-logo' src={logo} alt='' />
              ) : null}
              <span className='payment-type-section__bank-title'>{gatewayLabel(provider)}</span>
              {isDefault ? (
                <span className='payment-type-section__bank-tag'>پیشنهادی</span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <>
      <div className={`payment-type-section rtl ${loading ? 'payment-type-section--loading' : ''}`}>
        <div className='payment-type-section__title'>انتخاب نحوه پرداخت</div>

        <div className='payment-type-section__options' role='radiogroup' aria-label='نحوه پرداخت'>
          {/* آنلاین */}
          <div
            className={`payment-type-section__option ${
              showBanksForOnline ? 'payment-type-section__option--with-banks' : ''
            } ${payType === 'cashe' ? 'payment-type-section__option--selected' : ''} ${
              onlineDisabled ? 'payment-type-section__option--disabled' : ''
            }`}
            onClick={() => {
              if (!onlineDisabled) handlePaymentTypeChange('cashe');
            }}
          >
            <div className='payment-type-section__option-row'>
              <input
                onChange={(e) => handlePaymentTypeChange(e.target.value)}
                className='payment-type-section__input'
                type='radio'
                name='payType'
                checked={payType === 'cashe'}
                value='cashe'
                id='cashe'
                disabled={onlineDisabled}
              />
              <label
                className={`payment-type-section__label ${
                  onlineDisabled ? 'payment-type-section__label--disabled' : ''
                }`}
                htmlFor='cashe'
              >
                <div className='payment-type-section__icon'>
                  <CreditCard size={22} />
                </div>
                <span className='payment-type-section__text'>
                  <div className='payment-type-section__text-main'>پرداخت آنلاین</div>
                  {onlineDisabled ? (
                    <div className='payment-type-section__text-sub'>درگاه موقتاً غیرفعال است</div>
                  ) : selectedProvider && payType === 'cashe' ? (
                    <div className='payment-type-section__text-sub'>
                      درگاه {gatewayLabel(selectedProvider)}
                    </div>
                  ) : (
                    <div className='payment-type-section__text-sub'>
                      سقف هر پرداخت {formatGatewayLimit(maxAmountToman)}
                    </div>
                  )}
                </span>
                <div className='payment-type-section__checkmark' aria-hidden='true'>
                  <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
                    <path
                      d='M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z'
                      fill='currentColor'
                    />
                  </svg>
                </div>
              </label>
            </div>

            {showBanksForOnline ? renderGatewayBanks() : null}
          </div>

          {/* بیعانه — فقط وقتی در تنظیمات فعال است */}
          {depositEnabled ? (
          <div
            className={`payment-type-section__option ${
              showBanksForDeposit ? 'payment-type-section__option--with-banks' : ''
            } ${payType === 'deposit' ? 'payment-type-section__option--selected' : ''} ${
              isDepositDisabled ? 'payment-type-section__option--disabled' : ''
            }`}
            onClick={() => {
              if (!isDepositDisabled) handlePaymentTypeChange('deposit');
            }}
          >
            <div className='payment-type-section__option-row'>
              <input
                onChange={(e) => handlePaymentTypeChange(e.target.value)}
                disabled={isDepositDisabled}
                className='payment-type-section__input'
                checked={payType === 'deposit'}
                type='radio'
                name='payType'
                value='deposit'
                id='deposit'
              />
              <label
                className={`payment-type-section__label ${
                  isDepositDisabled ? 'payment-type-section__label--disabled' : ''
                }`}
                htmlFor='deposit'
              >
                <div className='payment-type-section__icon'>
                  <Wallet size={22} />
                </div>
                <span className='payment-type-section__text'>
                  <div className='payment-type-section__text-main'>پرداخت بیعانه</div>
                  <div className='payment-type-section__text-sub'>
                    {depositPrice <= 0 ? (
                      'در دسترس نیست'
                    ) : (
                      <>
                        مبلغ بیعانه:{' '}
                        <NumberFormat
                          value={depositPrice}
                          displayType='text'
                          thousandSeparator={true}
                          className='payment-type-section__price'
                        />{' '}
                        تومان
                        {selectedProvider && payType === 'deposit' && !onlineDisabled ? (
                          <> — درگاه {gatewayLabel(selectedProvider)}</>
                        ) : null}
                      </>
                    )}
                  </div>
                </span>
                <div className='payment-type-section__checkmark' aria-hidden='true'>
                  <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
                    <path
                      d='M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z'
                      fill='currentColor'
                    />
                  </svg>
                </div>
              </label>
            </div>

            {showBanksForDeposit ? renderGatewayBanks() : null}
          </div>
          ) : null}

          {/* فیش واریز — فقط وقتی فعال است */}
          {transferEnabled ? (
            <div
              className={`payment-type-section__option ${
                payType === 'transfer_receipt' ? 'payment-type-section__option--selected' : ''
              }`}
              onClick={() => handlePaymentTypeChange('transfer_receipt')}
            >
              <input
                onChange={(e) => handlePaymentTypeChange(e.target.value)}
                className='payment-type-section__input'
                type='radio'
                name='payType'
                checked={payType === 'transfer_receipt'}
                value='transfer_receipt'
                id='transfer_receipt'
              />
              <label className='payment-type-section__label' htmlFor='transfer_receipt'>
                <div className='payment-type-section__icon'>
                  <Receipt size={22} />
                </div>
                <span className='payment-type-section__text'>
                  <div className='payment-type-section__text-main'>فیش واریز</div>
                  <div className='payment-type-section__text-sub'>
                    شماره حساب به‌زودی پیامک می‌شود؛ بعد از واریز فیش را از سفارش‌های پروفایل ارسال کنید
                  </div>
                </span>
                <div className='payment-type-section__checkmark' aria-hidden='true'>
                  <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
                    <path
                      d='M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z'
                      fill='currentColor'
                    />
                  </svg>
                </div>
              </label>
            </div>
          ) : null}

          {ENABLE_GOLD_WALLET_IN_CART && walletGearmValue > 0 && (
            <div
              className={`payment-type-section__option ${
                goldGramCartUsage > 0 ? 'payment-type-section__option--selected' : ''
              }`}
              onClick={() => {
                if (goldGramCartUsage > 0) {
                  dispatch(set_gold_gram_usage(0));
                } else {
                  dispatch(set_gold_gram_usage(walletGearmValue));
                }
              }}
            >
              <input
                className='payment-type-section__input'
                type='checkbox'
                checked={goldGramCartUsage > 0}
                readOnly
                tabIndex={-1}
              />
              <label className='payment-type-section__label'>
                <div className='payment-type-section__icon'>
                  <Coins size={22} />
                </div>
                <span className='payment-type-section__text'>
                  <div className='payment-type-section__text-main'>استفاده از موجودی گرمی</div>
                  <div className='payment-type-section__text-sub'>
                    {parseFloat(walletGearmValue).toFixed(2)} گرم
                  </div>
                </span>
                <div className='payment-type-section__checkmark' aria-hidden='true'>
                  <svg width='16' height='16' viewBox='0 0 24 24' fill='none'>
                    <path
                      d='M9 16.17L4.83 12L3.41 13.41L9 19L21 7L19.59 5.59L9 16.17Z'
                      fill='currentColor'
                    />
                  </svg>
                </div>
              </label>
            </div>
          )}
        </div>
      </div>

      <div className='payment-type-section__footer'>
        <Button
          type='button'
          className='confirm-button'
          disabled={!canContinue}
          onClick={handleClick}
        >
          <span className='confirm-text'>مرحله بعد</span>
        </Button>
      </div>
    </>
  );
};

export default PaymentTypeSection;
